import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useQueryParams } from "../helper/HOC/useQueryParams";
import { getCompanyJobs } from "../redux/slices/jobSlices/getCompanyJobsSlice";
import { getCompanyDetail } from "../redux/slices/jobSlices/getCompanyDetailSlice";
import NotFound from "./NotFound";
import Loader from "../components/UI/Loader";
import { Dialog, Pagination, PaginationItem, TextField, Box } from "@mui/material";
import EcnButtonFilledV2 from "../components/StyledComponents/EcnButtonFilledV2";
import {
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography
} from "@mui/material";
import { getOrganisationJobDetails } from "../redux/slices/jobSlices/getOrganisationJobDetailSlice";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;
const ADMIN_BASE =
  GATEWAY_BASE === "https://api.dev.ecndev.io"
    ? "https://admin.dev.ecndev.io"
    : "https://admin.onefinnet.com";

const CompanyJobListV2 = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, updateQueryParams] = useQueryParams();
  const [search, setSearch] = useState("");
  const { companyName } = useParams<{ companyName: string }>();


  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = useState<number>(initialPage);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [logo, setLogo] = useState("");
  const [showWaterMark, setShowWaterMark] = useState(true);
  const [fontStyle, setFontStyle] = useState("Plus Jakarta Sans");
  const [borderRadius, setBorderRadius] = useState("12px");
  const [brandColor, setBrandColor] = useState("#8A32E2");
  const [showPromo, setShowPromo] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [industrySearch, setIndustrySearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [selectedExperiences, setSelectedExperiences] = useState<string[]>([]);
  const [marginLeft, setMarginLeft] = useState(window?.innerWidth <= 768 ? "0px" : "-48px");
  const [isWindow, setIsWindow] = useState(window?.innerWidth <= 768 ? false : true);

  const { companyDetails, loading } = useAppSelector(
    (state) => state.getCompanyDetails,
  );
  const { jobList, loading: jobListLoading } = useAppSelector((state) => state.getCompanyJobs);
  const { userInfo } = useAppSelector((state) => state.userDetails);
  const { orgJobDetails } = useAppSelector((state) => state.getOrganisationJobDetails);

  useEffect(() => {
    if (companyName) {
      dispatch(getCompanyDetail(companyName));
    }
  }, [companyName, dispatch]);

  useEffect(() => {
    if (companyDetails) {
      dispatch(getOrganisationJobDetails(companyDetails.org_id));
    }
  }, [companyDetails, dispatch]);

  useEffect(() => {
    if (companyDetails) {
      setName(companyDetails?.title || "");
      setWebsite(companyDetails?.website_link || "");
      setLogo(companyDetails?.display_picture || null);
      setShowWaterMark(companyDetails?.show_watermark);
      setFontStyle(companyDetails?.font_style || "Plus Jakarta Sans");
      setBorderRadius(companyDetails?.border_radius || "12px");
      setBrandColor(companyDetails?.color || "#8A32E2");
    }
  }, [companyDetails]);

  useEffect(() => {
    if (!companyName) return;

    const timeoutId = setTimeout(() => {
      dispatch(
        getCompanyJobs({
          skip: (page - 1) * 9,
          limit: 9,
          firm: companyName,
          q: search,
          userId: userInfo?._id,
          filters: {
            industries: selectedIndustries,
            jobTypes: selectedJobTypes,
            experiences: selectedExperiences,
            locations: selectedLocations,
          },
        })
      );
    }, search ? 1000 : 0);

    return () => clearTimeout(timeoutId);
  }, [companyName, page, userInfo, search, selectedIndustries, selectedJobTypes, selectedExperiences, selectedLocations, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setMarginLeft(window?.innerWidth <= 768 ? "0px" : "-48px");
      setIsWindow(window?.innerWidth <= 768 ? false : true);
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const applyHandler = (jobId: string, canNotApply: boolean, closed: boolean) => {
    if (canNotApply || closed) return;
    navigate(`job/${jobId}`);
  };

  const pageHandler = (e: any, page: any) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    updateQueryParams({ page: page });
    setPage(page);
  };

  const hexToRGBA = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      {loading ? (
        <div className="loader-container">
          <Loader color={brandColor} />
        </div>
      ) : companyDetails ? (
        <div className="jobs-list">
          <style>
            {`
              .jobs-list * {
                font-family: ${fontStyle} !important;
                border-radius: ${borderRadius} !important;
              }
            `}
          </style>
          <div className="organisation-heading">
            <div className="left">
              <img src={logo} alt="Company logo" />
              <p className="organisation-name">
                {name}
              </p>
            </div>
            {website && (
              <div className="right">
                <a
                  href={website.startsWith("http") ? website : `https://${website}`}
                  className="organisation-name"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          <div className="organisation-body">
            <div className="hero">
              <div className="heading">
                <span style={{ color: brandColor }}>Jobs</span> at <span className="name">{name}</span>
              </div>
              <div className="description">
                Explore exciting career opportunities at <span className="name">{name}</span> and join a team that values innovation and growth.
              </div>
              <div className="row">
                <div className="icon-input"
                  style={{
                    borderRadius: borderRadius
                  }}
                >
                  <Icon icon="mingcute:search-line" />
                  <input
                    type="text"
                    placeholder="Search for Jobs"
                    name="website"
                    value={search}
                    style={{
                      borderRadius: borderRadius
                    }}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="jobs">
              <div className="jobs-header">
                <motion.p
                  key={[selectedJobTypes, selectedLocations, selectedExperiences].join("-")}
                  initial={{ opacity: 0, x: 45 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  All
                  {selectedJobTypes && selectedJobTypes.length > 0 && (
                    <>
                      {" "}
                      <span className="search-query">"{selectedJobTypes.join(", ")}"</span>
                    </>
                  )} Jobs
                  {search && (
                    <>
                      {" "}
                      with <span className="search-query">"{search}"</span>
                    </>
                  )}
                  {selectedLocations && selectedLocations.length > 0 && (
                    <>
                      {" "}
                      in <span className="search-query">"{selectedLocations.join(", ")}"</span>
                    </>
                  )}
                  {selectedIndustries && selectedIndustries.length > 0 && (
                    <>
                      {" "}
                      in <span className="search-query">"{selectedIndustries.join(", ")} industries"</span>
                    </>
                  )}
                  {selectedExperiences && selectedExperiences.length > 0 && (
                    <>
                      {" "}
                      of{" "}
                      <span className="search-query">
                        "{selectedExperiences.join(", ")} experiences"
                      </span>
                    </>
                  )}
                </motion.p>
                <div className="jobs-filter"
                  style={{
                    borderRadius: borderRadius
                  }}
                  onClick={handleClick}
                >
                  <div className="filter-icon">
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Icon icon="mingcute:filter-line" />
                      {(selectedExperiences?.length > 0 ||
                        selectedIndustries?.length > 0 ||
                        selectedJobTypes?.length > 0 ||
                        selectedLocations?.length > 0) && (
                          <Box
                            className="dot-indicator"
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 1,
                              width: 7,
                              height: 7,
                              backgroundColor: brandColor,
                              border: "2px solid #fff",
                              borderRadius: "50%",
                            }}
                          />
                        )}
                    </Box>
                  </div>
                  <p>Filter</p>
                </div>
                <Menu
                  id="filter-menu"
                  className="jobs-filter-menu-container"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  PaperProps={{
                    style: {
                      maxHeight: "500px",
                      minWidth: "320px",
                      padding: "0",
                      borderRadius: borderRadius,
                      border: "1px solid #E4E7EC",
                      boxShadow: "0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03)", // Remove box shadow
                      marginLeft: marginLeft || "0px",
                      marginTop: "10px",
                      fontFamily: fontStyle,
                    }
                  }}
                  MenuListProps={{
                    autoFocusItem: false,
                    sx: {
                      paddingTop: 0,
                      paddingBottom: 0,
                    }
                  }}
                >
                  {[
                    ...(orgJobDetails?.industries?.length ? ["Industry"] : []),
                    "Job Type",
                    "Experience",
                    ...(orgJobDetails?.cities?.length ? ["Location"] : []),
                  ].map((section) => (
                    <MenuItem
                      disableRipple
                      key={section}
                      className="menu-item"
                      sx={{
                        padding: "0px",
                        width: "100%",
                        display: "block",
                        borderRadius: "0",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <Accordion
                        disableGutters
                        expanded={expandedAccordion === section}
                        onChange={() =>
                          setExpandedAccordion(prev => prev === section ? false : section)
                        }
                        sx={{
                          boxShadow: "none",
                          border: "none",
                          backgroundColor: "transparent",
                          "&::before": { display: "none" },
                          marginBottom: "0px",
                          borderBottom: "1px solid #EAECF0",
                          borderRadius: 0,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<Icon icon="mingcute:down-line" />}
                          sx={{
                            minHeight: "44px",
                            paddingX: 2,
                            "& .MuiAccordionSummary-content": {
                              margin: 0
                            },
                            "& .MuiAccordionSummary-expandIconWrapper": {
                              color: "#09244B",
                              fontSize: "24px",
                            }
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 600,
                              color: "#1D2939",
                              fontFamily: "Plus Jakarta Sans",
                            }}
                          >{section}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FormGroup>
                            {section === "Industry" && (
                              <>
                                <Box>
                                  <TextField
                                    size="small"
                                    placeholder="Search Industries"
                                    value={industrySearch}
                                    onChange={(e) => setIndustrySearch(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                        <Icon icon="mingcute:search-line" style={{ color: "#D0D5DD", marginRight: "8px", fontSize: "20px" }} />
                                      ),
                                    }}
                                    sx={{
                                      mb: 1,
                                      borderRadius: borderRadius,
                                      backgroundColor: "#F9FAFB",
                                      outline: "none",
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: borderRadius,
                                        paddingLeft: "8px",
                                      },
                                    }}
                                  />
                                </Box>
                                {
                                  orgJobDetails &&
                                  orgJobDetails?.industries?.filter((label: any) =>
                                    label.toLowerCase().includes(industrySearch.toLowerCase())
                                  )
                                    .map((label: any) => (
                                      <FormControlLabel
                                        key={label}
                                        sx={{ mb: 1, margin: 0 }}
                                        control={
                                          <Checkbox
                                            name={label}
                                            onChange={(e) => {
                                              const value = e.target.name;
                                              setSelectedIndustries((prev) =>
                                                prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                                              );
                                            }}
                                            checked={selectedIndustries.includes(label)}
                                            sx={{
                                              color: "#D0D5DD",
                                              '&.Mui-checked': {
                                                color: brandColor,
                                              },
                                              '& .MuiSvgIcon-root': {
                                                borderRadius: "6px",
                                                stroke: "#D0D5DD",
                                                strokeWidth: "0.1",
                                              },
                                            }}
                                          />
                                        }
                                        label={label}
                                        componentsProps={{
                                          typography: {
                                            sx: {
                                              color: "#1D2939",
                                              fontFamily: "Plus Jakarta Sans",
                                              fontSize: "14px",
                                              fontWeight: "500",
                                            },
                                          },
                                        }}
                                      />
                                    ))}
                              </>
                            )}
                            {section === "Job Type" && ["Full Time", "Internship", "Part Time", "Contractual"].map((label) => (
                              <FormControlLabel
                                key={label}
                                sx={{ mb: 1, margin: 0 }}
                                control={
                                  <Checkbox
                                    name={label}
                                    onChange={(e) => {
                                      const value = e.target.name;
                                      setSelectedJobTypes((prev) =>
                                        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                                      );
                                    }}
                                    checked={selectedJobTypes.includes(label)}
                                    sx={{
                                      color: "#D0D5DD",
                                      '&.Mui-checked': {
                                        color: brandColor,
                                      },
                                      '& .MuiSvgIcon-root': {
                                        borderRadius: "6px",
                                        stroke: "#D0D5DD",
                                        strokeWidth: "0.1",
                                      },
                                    }}
                                  />
                                }
                                label={label}
                                componentsProps={{
                                  typography: {
                                    sx: {
                                      color: "#1D2939",
                                      fontFamily: "Plus Jakarta Sans",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                    },
                                  },
                                }}
                              />
                            ))}
                            {section === "Experience" && ["0-2 years", "2-4 years", "4-8 years", "8-12 years", "12+ years"].map((label) => (
                              <FormControlLabel
                                key={label}
                                sx={{ mb: 1, margin: 0 }}
                                control={
                                  <Checkbox
                                    name={label}
                                    onChange={(e) => {
                                      const value = e.target.name;
                                      setSelectedExperiences((prev) =>
                                        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                                      );
                                    }}
                                    checked={selectedExperiences.includes(label)}
                                    sx={{
                                      color: "#D0D5DD",
                                      '&.Mui-checked': {
                                        color: brandColor,
                                      },
                                      '& .MuiSvgIcon-root': {
                                        borderRadius: "6px",
                                        stroke: "#D0D5DD",
                                        strokeWidth: "0.1",
                                      },
                                    }}
                                  />
                                }
                                label={label}
                                componentsProps={{
                                  typography: {
                                    sx: {
                                      color: "#1D2939",
                                      fontFamily: "Plus Jakarta Sans",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                    },
                                  },
                                }}
                              />
                            ))}
                            {section === "Location" &&
                              orgJobDetails &&
                              orgJobDetails?.cities && (
                                <>
                                  <TextField
                                    size="small"
                                    placeholder="Search Locations"
                                    value={locationSearch}
                                    onChange={(e) => setLocationSearch(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                        <Icon icon="mingcute:search-line" style={{ color: "#D0D5DD", marginRight: "8px", fontSize: "20px" }} />
                                      ),
                                    }}
                                    sx={{
                                      mb: 1,
                                      borderRadius: borderRadius,
                                      backgroundColor: "#F9FAFB",
                                      outline: "none",
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: borderRadius,
                                        paddingLeft: "8px",
                                      },
                                    }}
                                  />
                                  {
                                    orgJobDetails &&
                                    orgJobDetails?.cities?.filter((label: any) =>
                                      label.toLowerCase().includes(locationSearch.toLowerCase())
                                    )
                                      .map((label: any) => (
                                        <FormControlLabel
                                          key={label}
                                          sx={{ mb: 1, margin: 0 }}
                                          control={
                                            <Checkbox
                                              name={label}
                                              onChange={(e) => {
                                                const value = e.target.name;
                                                setSelectedLocations((prev) =>
                                                  prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                                                );
                                              }}
                                              checked={selectedLocations.includes(label)}
                                              sx={{
                                                color: "#D0D5DD",
                                                '&.Mui-checked': {
                                                  color: brandColor,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                  borderRadius: "6px",
                                                  stroke: "#D0D5DD",
                                                  strokeWidth: "0.1",
                                                },
                                              }}
                                            />
                                          }
                                          label={label}
                                          componentsProps={{
                                            typography: {
                                              sx: {
                                                color: "#1D2939",
                                                fontFamily: "Plus Jakarta Sans",
                                                fontSize: "14px",
                                                fontWeight: "500",
                                              },
                                            },
                                          }}
                                        />
                                      ))
                                  }
                                </>
                              )}
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    </MenuItem>
                  ))}
                </Menu>
              </div>
              <hr className="divider" />
              {jobList?.jobs?.length === 0 && (
                <div className="job-list-fallback">
                  <img
                    src="https://s1.ecnstatic.com/ecn/images/enterprise/People_searching_for_information_online.svg"
                    alt="People_searching_for_information_online"
                    style={{ width: 200, height: 200 }}
                  />
                  <div className="fallback-message" style={{ marginTop: 50 }}>
                    No jobs found!
                  </div>
                </div>
              )}
              <div className="jobs-cards">
                {jobListLoading ? (
                  Array.from({ length: 9 }).map((_, idx) => (
                    <div key={idx} className="job-card loading-card" style={{ borderRadius }}>
                      <div className="shimmer loader-title" />
                      <div className="loader-tags">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="shimmer loader-tag" />
                        ))}
                      </div>
                      <div className="shimmer loader-footer" />
                    </div>
                  ))
                ) : (
                  jobList?.jobs?.map((job: any) => {
                    const experience =
                      job.experience_required || job.max_experience_required
                        ? `${job.experience_required || 0} – ${job.max_experience_required || job.experience_required || 0} years of Experience`
                        : "Experience Not Specified";
                    const role = job.job_roles?.[0] || "Role Not Specified";
                    const type = job.role_type || "Type Not Specified";
                    const industry = job.industries?.[0] || "Industry";
                    const city = job.address?.cities?.[0] || "Location";

                    return (
                      <div
                        key={job._id}
                        className="job-card"
                        style={{ borderRadius: borderRadius }}
                        onClick={() => applyHandler(job._id, false, job.closed)}
                      >
                        <div className="job-header">
                          <h3 className="job-title">{job.job_title}</h3>
                          {job.closed &&
                            <div
                              className="closed-job-chip"
                              style={{
                                borderRadius: borderRadius,
                                color: brandColor,
                                borderColor: hexToRGBA(brandColor, 0.5),
                              }}
                            >
                              Closed
                            </div>
                          }
                        </div>
                        <div className="job-tags">
                          <span className="tag" style={{ borderRadius: borderRadius }}>
                            <Icon icon="mingcute:necktie-line" style={{ color: brandColor }} /> {role}
                          </span>
                          <span className="tag" style={{ borderRadius: borderRadius }}>
                            <Icon icon="mingcute:suitcase-line" style={{ color: brandColor }} /> {type}
                          </span>
                          <span className="tag" style={{ borderRadius: borderRadius }}>
                            <Icon icon="mingcute:time-line" style={{ color: brandColor }} /> {experience}
                          </span>
                          <span className="tag" style={{ borderRadius: borderRadius }}>
                            <Icon icon="mingcute:building-2-line" style={{ color: brandColor }} /> {industry}
                          </span>
                          <span className="tag" style={{ borderRadius: borderRadius }}>
                            <Icon icon="mingcute:location-line" style={{ color: brandColor }} /> {city}
                          </span>
                        </div>
                        <p className="explore-job">
                          Explore this job <Icon icon="mingcute:right-line" />
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
              <hr className="divider" style={{ marginBlock: 0 }} />
              {jobList?.jobs?.length !== 0 && jobList?.total_count > 9 && (
                <div className="pagination-wrapper">
                  <style>
                    {`
                      .pagination-wrapper * {
                        font-family: ${fontStyle} !important;
                        border-radius: ${borderRadius} !important;
                      }

                      .pagination-wrapper .MuiPaginationItem-root.Mui-selected {
                        background-color: ${hexToRGBA(brandColor, 0.1)} !important;
                        color: ${brandColor} !important;

                        &:hover {
                          background-color: ${hexToRGBA(brandColor, 0.1)} !important;
                        }
                      }

                      .pagination-wrapper .MuiPaginationItem-root:hover {
                        background-color: ${hexToRGBA(brandColor, 0.05)} !important;
                      }

                      .pagination-wrapper .MuiPaginationItem-ellipsis {
                        color: ${brandColor} !important;
                      }

                    `}
                  </style>
                  <Pagination
                    page={page}
                    onChange={pageHandler}
                    count={
                      jobList?.total_count ? Math.ceil(jobList.total_count / 9) : 1
                    }
                    className="job-pagination custom-pagination"
                    hidePrevButton={false}
                    hideNextButton={false}
                    showFirstButton={false}
                    showLastButton={false}
                    siblingCount={1}
                    boundaryCount={1}
                    renderItem={(item) => (
                      <PaginationItem
                        components={{
                          previous: () => <span
                          >
                            <Icon
                              icon="mingcute:left-line"
                            />
                            {isWindow && (
                              "Previous"
                            )}
                          </span>, next: () => <span
                          >
                            {isWindow && (
                              "Next"
                            )}
                            <Icon
                              icon="mingcute:right-line"
                            />
                          </span>
                        }}
                        {...item}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>
          {
            showWaterMark && (
              !showPromo ? (
                <div
                  className="floating-container"
                  onClick={() => setShowPromo(true)}
                >
                  <p>Created in</p>
                  <img src="/logo.svg" alt="Elevate 2.0 icon" />
                </div>
              ) : (
                <Dialog
                  open={showPromo}
                  onClose={() => setShowPromo(false)}
                  maxWidth="md"
                  className="promo-dialog"
                  PaperProps={{
                    sx: {
                      position: "absolute",
                      bottom: "10px",
                      right: "10px",
                      borderRadius: borderRadius,
                      fontFamily: fontStyle,
                      m: 0,
                    },
                  }}
                  slotProps={{
                    backdrop: {
                      style: {
                        boxShadow: "none",
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                >
                  <div className="promo-dialog-container"
                    style={{
                      borderRadius: borderRadius,
                    }}
                  >
                    <div>
                      <img
                        src="https://ecn.blob.core.windows.net/ecn/images/talent ad.png"
                        alt=""
                        className="ad-img"
                      />
                    </div>
                    <div className="text-container">
                      <h1>Post a Job today</h1>
                      <p>
                        With Onefinnet Talent, you can quickly post a job and find
                        the right candidates.
                      </p>
                    </div>
                    <EcnButtonFilledV2
                      className="featured-button"
                      sx={{ width: "200px", borderRadius: borderRadius }}
                      onClick={() => {
                        window.open(
                          ADMIN_BASE + "/signup?client=third-party&coupon=FINFIRST",
                          "_blank"
                        );
                      }}
                    >
                      Post a Featured Job
                    </EcnButtonFilledV2>
                    <EcnButtonFilledV2
                      className="try-button"
                      sx={{ width: "200px", borderRadius: borderRadius }}
                      onClick={() => {
                        window.open(ADMIN_BASE + "/signup", "_blank");
                      }}
                    >
                      Try Talent for Free
                    </EcnButtonFilledV2>
                  </div>
                </Dialog>
              )
            )
          }
        </div>
      ) : (
        <NotFound />
      )}</>
  );
};

export default CompanyJobListV2;
